import { supabase } from "@/integrations/supabase/client";

export interface ProfileCompletenessScore {
  overall: number;
  sections: {
    basicInfo: number;
    avatar: number;
    bio: number;
    contact: number;
    verification: number;
    tracks: number;
  };
  missingFields: string[];
  suggestions: string[];
}

export async function analyzeProfileCompleteness(
  userId: string,
  userRole: string,
): Promise<ProfileCompletenessScore> {
  const score: ProfileCompletenessScore = {
    overall: 0,
    sections: {
      basicInfo: 0,
      avatar: 0,
      bio: 0,
      contact: 0,
      verification: 0,
      tracks: 0,
    },
    missingFields: [],
    suggestions: [],
  };

  try {
    // Get public profile
    const { data: publicProfile } = await supabase
      .from("public_profiles")
      .select("*")
      .eq("id", userId)
      .single();

    // Get role-specific profile
    let roleProfile = null;
    if (userRole === "artist") {
      const { data } = await supabase
        .from("artist_profiles")
        .select("*")
        .eq("id", userId)
        .single();
      roleProfile = data;
    } else if (userRole === "sponsor") {
      const { data } = await supabase
        .from("sponsor_profiles")
        .select("*")
        .eq("id", userId)
        .single();
      roleProfile = data;
    } else if (userRole === "fan") {
      const { data } = await supabase
        .from("fan_profiles")
        .select("*")
        .eq("id", userId)
        .single();
      roleProfile = data;
    }

    // Analyze basic info (30% weight)
    let basicInfoScore = 0;
    if (publicProfile?.display_name) basicInfoScore += 50;
    if (publicProfile?.username) basicInfoScore += 50;
    score.sections.basicInfo = basicInfoScore;

    if (!publicProfile?.display_name) {
      score.missingFields.push("display_name");
      score.suggestions.push(
        "Add your display name to help others recognize you",
      );
    }
    if (!publicProfile?.username) {
      score.missingFields.push("username");
      score.suggestions.push("Choose a unique username for your profile URL");
    }

    // Analyze avatar (10% weight)
    score.sections.avatar = publicProfile?.avatar_url ? 100 : 0;
    if (!publicProfile?.avatar_url) {
      score.missingFields.push("avatar_url");
      score.suggestions.push(
        "Upload a profile picture to personalize your account",
      );
    }

    // Analyze bio (15% weight)
    score.sections.bio = publicProfile?.bio ? 100 : 0;
    if (!publicProfile?.bio) {
      score.missingFields.push("bio");
      score.suggestions.push("Write a bio to tell others about yourself");
    }

    // Analyze contact info (15% weight)
    const hasPhone = !!(publicProfile?.verified_phone || roleProfile?.phone);
    let contactScore = 0;
    if (publicProfile?.location) contactScore += 50;
    if (hasPhone) contactScore += 50;
    score.sections.contact = contactScore;

    if (!publicProfile?.location) {
      score.missingFields.push("location");
      score.suggestions.push("Add your location to connect with local users");
    }
    if (!hasPhone) {
      score.missingFields.push("phone");
      score.suggestions.push("Add your phone number for account security");
    }

    // Analyze verification (20% weight for artists/sponsors, 10% for fans)
    if (userRole === "artist") {
      // For artists, check KYC verification status
      const kycStatus = roleProfile?.kyc_status;
      score.sections.verification = kycStatus === "approved" ? 100 : 0;
      if (kycStatus !== "approved") {
        score.missingFields.push("kyc_verification");
        score.suggestions.push(
          "Complete KYC verification to enable cashouts and unlock all features",
        );
      }
    } else if (userRole === "sponsor") {
      score.sections.verification = publicProfile?.is_verified ? 100 : 0;
      if (!publicProfile?.is_verified) {
        score.missingFields.push("verification");
        score.suggestions.push(
          "Complete verification to unlock all platform features",
        );
      }
    } else {
      score.sections.verification = 100; // Fans don't need verification
    }

    // Analyze tracks (10% weight for artists, 0% for others)
    if (userRole === "artist") {
      const trackCount = roleProfile?.track_count || 0;
      score.sections.tracks = trackCount > 0 ? 100 : 0;
      if (trackCount === 0) {
        score.missingFields.push("tracks");
        score.suggestions.push(
          "Upload your first track to start building your audience",
        );
      }
    } else {
      score.sections.tracks = 100; // Non-artists don't need tracks
    }

    // Calculate overall score based on role
    if (userRole === "artist") {
      score.overall = Math.round(
        score.sections.basicInfo * 0.3 +
          score.sections.avatar * 0.1 +
          score.sections.bio * 0.15 +
          score.sections.contact * 0.15 +
          score.sections.verification * 0.2 +
          score.sections.tracks * 0.1,
      );
    } else if (userRole === "sponsor") {
      score.overall = Math.round(
        score.sections.basicInfo * 0.35 +
          score.sections.avatar * 0.15 +
          score.sections.bio * 0.2 +
          score.sections.contact * 0.1 +
          score.sections.verification * 0.2,
      );
    } else {
      score.overall = Math.round(
        score.sections.basicInfo * 0.4 +
          score.sections.avatar * 0.2 +
          score.sections.bio * 0.25 +
          score.sections.contact * 0.15,
      );
    }

    return score;
  } catch (error) {
    console.error("Error analyzing profile completeness:", error);
    return score;
  }
}
